import type { EmployeeDetail, InvitationStatus, Passkey } from "@cleanbudget/contract/employee";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import {
	IconAlertCircle,
	IconLoader2,
	IconMail,
	IconTrash,
	IconUserCheck,
	IconUserOff,
} from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DetailFacts, DetailLayout, DetailSection } from "@/components/detail-layout";
import { PageHeader } from "@/components/page-header";
import { AddPasskeyButton } from "@/components/sign-in/add-passkey-button";
import { StatusBadge } from "@/components/status-badge";
import { apiErrorTag } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { orpc } from "@/lib/orpc";
import { statusTone } from "./-components/status";

const employeeQuery = (id: string) => orpc.employee.get.queryOptions({ input: { id } });

/**
 * One employee: their passkeys (add one on this device to your own account, remove any but the
 * last way in) and whether they have a password, a fresh enrolment link by mail, and deactivating, behind a confirm dialog.
 */
export const Route = createFileRoute("/_admin/settings/employee/$id")({
	loader: ({ context, params }) => context.queryClient.ensureQueryData(employeeQuery(params.id)),
	component: EmployeePage,
});

function EmployeePage() {
	const { t } = useTranslation("employee");
	const { t: tCommon } = useTranslation();
	const { id } = Route.useParams();
	const { session } = Route.useRouteContext();
	const { data: employee } = useSuspenseQuery(employeeQuery(id));
	const isSelf = session.employee.id === employee.id;
	const deactivated = employee.status === "deactivated";

	return (
		<DetailLayout
			header={
				<PageHeader
					back={<BackLink to="/settings/employee">{t("title")}</BackLink>}
					title={employee.name}
					description={employee.email}
				/>
			}
			aside={
				<>
					<DetailSection title={t("detail.facts")}>
						<DetailFacts
							facts={[
								{
									label: t("column.status"),
									value: (
										<StatusBadge tone={statusTone(employee.status)}>
											{t(`status.${employee.status}`)}
										</StatusBadge>
									),
								},
								{
									label: t("detail.password"),
									value: employee.hasPassword ? t("detail.passwordSet") : t("detail.passwordNotSet"),
								},
								{ label: t("column.created"), value: formatDateTime(employee.createdAt) },
								{
									label: t("column.lastLogin"),
									value: employee.lastLoginAt ? formatDateTime(employee.lastLoginAt) : tCommon("never"),
								},
								...(employee.deactivatedAt
									? [{ label: t("detail.deactivatedAt"), value: formatDateTime(employee.deactivatedAt) }]
									: []),
							]}
						/>
					</DetailSection>
					{deactivated ? (
						<ReactivateSection employee={employee} />
					) : (
						<InvitationSection employee={employee} />
					)}
					{deactivated || isSelf ? null : <DeactivateSection employee={employee} />}
				</>
			}
		>
			<PasskeysSection employee={employee} isSelf={isSelf} />
		</DetailLayout>
	);
}

function PasskeysSection({ employee, isSelf }: { employee: EmployeeDetail; isSelf: boolean }) {
	const { t } = useTranslation("employee");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.employee.key() });
	const remove = useMutation({ ...orpc.employee.removePasskey.mutationOptions(), onSuccess: invalidate });
	// The last way in stays: the last passkey goes only when there is a password.
	const onlyOne = employee.passkeys.length <= 1 && !employee.hasPassword;

	const columns: readonly Column<Passkey>[] = [
		{
			id: "passkey",
			header: t("passkeys.column.passkey"),
			cell: (passkey) => (
				<span className="flex flex-col">
					<span className="font-medium">
						{t("passkeys.name", { number: employee.passkeys.indexOf(passkey) + 1 })}
					</span>
					<span className="text-xs text-muted-foreground">
						{passkey.backedUp ? t("passkeys.synced") : t("passkeys.device")}
					</span>
				</span>
			),
		},
		{
			id: "createdAt",
			header: t("passkeys.column.created"),
			secondary: true,
			cell: (passkey) => <time dateTime={passkey.createdAt}>{formatDateTime(passkey.createdAt)}</time>,
		},
		{
			id: "lastUsedAt",
			header: t("passkeys.column.lastUsed"),
			cell: (passkey) =>
				passkey.lastUsedAt ? (
					<time dateTime={passkey.lastUsedAt}>{formatDateTime(passkey.lastUsedAt)}</time>
				) : (
					<span className="text-muted-foreground">{tCommon("never")}</span>
				),
		},
		{
			id: "actions",
			header: <span className="sr-only">{t("passkeys.column.actions")}</span>,
			align: "right",
			cell: (passkey) => (
				<ConfirmDialog
					trigger={
						<Button
							variant="ghost"
							size="icon-sm"
							disabled={onlyOne}
							aria-label={t("passkeys.remove.button", {
								name: t("passkeys.name", { number: employee.passkeys.indexOf(passkey) + 1 }),
							})}
						>
							<IconTrash aria-hidden />
						</Button>
					}
					title={t("passkeys.remove.title")}
					description={
						isSelf
							? t("passkeys.remove.descriptionSelf")
							: t("passkeys.remove.description", { name: employee.name })
					}
					confirmLabel={t("passkeys.remove.confirm")}
					onConfirm={() => remove.mutateAsync({ id: employee.id, passkeyId: passkey.id })}
					errorMessage={(error) =>
						apiErrorTag(error) === "LastPasskey" ? t("passkeys.lastOne") : tCommon("confirm.failed")
					}
				/>
			),
		},
	];

	return (
		<DetailSection
			title={t("passkeys.title")}
			description={
				employee.status === "deactivated"
					? t("passkeys.deactivated")
					: onlyOne && employee.passkeys.length === 1
						? t("passkeys.lastOne")
						: t("passkeys.description")
			}
			actions={isSelf ? <AddPasskeyButton /> : null}
		>
			<DataTable
				label={t("passkeys.title")}
				columns={columns}
				rows={employee.passkeys}
				rowKey={(passkey) => passkey.id}
				empty={<p className="p-4 text-sm text-muted-foreground">{t("passkeys.none")}</p>}
			/>
		</DetailSection>
	);
}

function InvitationSection({ employee }: { employee: EmployeeDetail }) {
	const { t } = useTranslation("employee");
	const { t: tCommon } = useTranslation();
	const [result, setResult] = useState<InvitationStatus | "error">();
	const resend = useMutation(orpc.employee.resendInvitation.mutationOptions());
	const invited = employee.status === "invited";

	const send = async () => {
		setResult(undefined);
		try {
			setResult((await resend.mutateAsync({ id: employee.id })).invitation.status);
		} catch {
			setResult("error");
		}
	};

	return (
		<DetailSection
			title={invited ? t("invitation.titleInvited") : t("invitation.titleActive")}
			description={invited ? t("invitation.descriptionInvited") : t("invitation.descriptionActive")}
		>
			<div>
				<Button variant="outline" onClick={send} disabled={resend.isPending}>
					{resend.isPending ? <IconLoader2 className="animate-spin" aria-hidden /> : <IconMail aria-hidden />}
					{t("invitation.send")}
				</Button>
			</div>
			{result === undefined ? null : result === "error" ? (
				<Alert variant="destructive">
					<IconAlertCircle aria-hidden />
					<AlertDescription>{tCommon("confirm.failed")}</AlertDescription>
				</Alert>
			) : (
				<p role="status" className="text-sm text-muted-foreground">
					{t(`invitation.result.${result}`, { email: employee.email })}
				</p>
			)}
		</DetailSection>
	);
}

function DeactivateSection({ employee }: { employee: EmployeeDetail }) {
	const { t } = useTranslation("employee");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const deactivate = useMutation({
		...orpc.employee.deactivate.mutationOptions(),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.employee.key() }),
	});

	return (
		<DetailSection title={t("deactivate.title")} description={t("deactivate.description")}>
			<div>
				<ConfirmDialog
					trigger={
						<Button variant="destructive">
							<IconUserOff aria-hidden />
							{t("deactivate.button")}
						</Button>
					}
					title={t("deactivate.confirmTitle", { name: employee.name })}
					description={t("deactivate.confirmDescription", { name: employee.name })}
					confirmLabel={t("deactivate.confirm")}
					onConfirm={() => deactivate.mutateAsync({ id: employee.id })}
					errorMessage={(error) =>
						apiErrorTag(error) === "CannotDeactivateYourself"
							? t("deactivate.self")
							: tCommon("confirm.failed")
					}
				/>
			</div>
		</DetailSection>
	);
}

function ReactivateSection({ employee }: { employee: EmployeeDetail }) {
	const { t } = useTranslation("employee");
	const queryClient = useQueryClient();
	const reactivate = useMutation({
		...orpc.employee.reactivate.mutationOptions(),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.employee.key() }),
	});
	const hasPasskeys = employee.passkeys.length > 0;

	return (
		<DetailSection title={t("reactivate.title")} description={t("reactivate.description")}>
			<div>
				<ConfirmDialog
					destructive={false}
					trigger={
						<Button variant="outline">
							<IconUserCheck aria-hidden />
							{t("reactivate.button")}
						</Button>
					}
					title={t("reactivate.confirmTitle", { name: employee.name })}
					description={
						hasPasskeys
							? t("reactivate.confirmWithPasskeys", { name: employee.name, count: employee.passkeys.length })
							: t("reactivate.confirmWithoutPasskeys", { name: employee.name })
					}
					confirmLabel={t("reactivate.confirm")}
					onConfirm={() => reactivate.mutateAsync({ id: employee.id })}
				/>
			</div>
		</DetailSection>
	);
}
